/**
 * The weekly digest — what a community asked last week, and what to make about it.
 *
 *   GET /v1/communities/{id}/digests            the weeks we hold
 *   GET /v1/communities/{id}/digests/latest     Monday morning's view
 *   GET /v1/communities/{id}/digests/{week}     one week, by any date inside it
 *   GET /v1/communities/{id}/topics             topics rolled up over a range
 *   GET /v1/communities/{id}/topics/{id}        one topic, every week it appeared
 *
 * REPLACED `questions.ts`, which read the clustering pipeline's `question_clusters`.
 * That pipeline, its page and its two `…/insights` blocks are gone; what survived of
 * that module is the AI ledger, now `@api/v1/ai`.
 *
 * WHY A TOPIC ID IS THE INTERESTING KEY. A `DigestTheme` is one week's slice; the
 * `topic_id` on it is stable across weeks, which is what makes `streak_weeks` and the
 * whole `/topics` rollup possible. The clustering module had no
 * such identity — every run rebuilt its clusters from scratch — so "asked three weeks
 * running" was not expressible in it at all.
 *
 * Timestamps are UTC-aware instants with a `+00:00` offset; parse them with `parseTs`.
 */
import { apiGet } from './http';
import type { ImageMedia, PostAuthor, VideoMedia } from './shapes';

/** How a theme's material stands against the community's own classroom. */
export type Coverage = 'covered' | 'thin' | 'missing';

/** What the owner should do about it. `make_content` is the common answer for a
 *  `covered` theme people are still asking about — covered means they did not find
 *  it, not that there is nothing to do. */
export type ActionKind = 'create_lesson' | 'update_lesson' | 'make_content' | 'none';

export interface DigestWeek {
  week_start: string;
  week_end: string;
  question_count: number;
  theme_count: number;
  generated_at: string;
}

/** One member's question, as a renderable card.
 *
 *  `text` and `permalink` are FROZEN into the snapshot — what was asked is history.
 *  `author` and `content` are hydrated LIVE, because who someone is (their name, their
 *  avatar) is a current fact about a person rather than a historical fact about the
 *  week, and a frozen avatar URL just means showing a stale one a year later.
 *
 *  `author` is the same five-field shape `<SkoolPost>`, `<SkoolComment>` and `<Avatar>`
 *  already take, so a quote renders as a real card rather than a blockquote. */
export interface DigestQuote {
  question_id?: number | null;
  /** The sentence the detector pulled out of the post or comment. */
  text?: string | null;
  /** 'post' | 'comment' — which row the thread highlights. */
  source?: string | null;
  /** The thread `<PostThread>` opens, and the comment to mark inside it. */
  post_id?: number | null;
  comment_id?: number | null;
  /** Deep-links to the comment via Skool's `?p=` 8-char form. */
  permalink?: string | null;
  asked_at?: string | null;
  author?: PostAuthor | null;
  /** What they actually wrote, read live from the post/comment. */
  title?: string | null;
  content?: string | null;
  upvotes: number;
  comment_count?: number | null;
  /** Uploads/gifs and embeds — `<SkoolPost>` renders these itself, and a post whose
   *  whole content is a screenshot is blank without them. */
  images?: ImageMedia[] | null;
  videos?: VideoMedia[] | null;
  /** For a comment: the post it answers. One line of context, not a whole post. */
  parent_title?: string | null;
}

/** One week of a topic's volume. Zero-filled, so the sparkline shape is honest. */
export interface DigestTrendPoint {
  week: string;
  count: number;
}

export interface DigestTheme {
  topic_id: number;
  title: string;
  rank: number;
  summary?: string | null;
  question_count: number;
  member_count: number;
  coverage?: Coverage | null;
  best_page_title?: string | null;
  action_kind?: ActionKind | null;
  action_text?: string | null;
  quotes: DigestQuote[];
  /** One point per week this community HAS A DIGEST FOR, oldest first, capped at
   *  twelve — the sparkline. Zero-filled across those weeks (a series built only from
   *  the weeks a topic appeared in is flat whatever happened), but never padded back
   *  to a fixed quarter: a week nobody digested is not a zero. */
  trend: DigestTrendPoint[];
  is_new: boolean;
  streak_weeks: number;
  delta_pct?: number | null;
  total_weeks: number;
  total_questions: number;
  status?: string | null;
}

export interface Digest {
  /** False when this community has no digest for the week — a thing to render, not
   *  an error. A community we have not run yet and one that asked nothing look the
   *  same from here, deliberately: neither has anything to show. */
  found: boolean;
  week_start?: string | null;
  week_end?: string | null;
  generated_at?: string | null;
  question_count?: number | null;
  themes: DigestTheme[];
  /** The week's questions no theme claimed — asides, small talk, a rhetorical
   *  question. Usually a handful; on a community that converses rather than asks it is
   *  the whole week, and then it is the only thing that explains a zero-theme digest. */
  ungrouped: DigestQuote[];
}

export interface TopicRollupRow {
  topic_id: number;
  title: string;
  status?: string | null;
  questions: number;
  peak_members: number;
  /** How many weeks this topic has appeared in at all, within the range asked for. */
  weeks_seen: number;
  first_week?: string | null;
  last_week?: string | null;
  /** The same series the week view draws — one point per week held. */
  trend: DigestTrendPoint[];
}

export interface TopicWeek {
  week_start: string;
  question_count: number;
  member_count: number;
  coverage?: Coverage | null;
  best_page_title?: string | null;
  action_kind?: ActionKind | null;
  action_text?: string | null;
  summary?: string | null;
  /** No `quotes`: `TopicHistory.posts`/`.comments` already carry every question behind
   *  this topic. A per-week list would be a second copy of a subset of them. */
}

export interface TopicMember {
  author?: PostAuthor | null;
  questions: number;
}

export interface TopicHistory {
  found: boolean;
  /** Everything behind this topic across every week — the topic page lists all of it,
   *  not the three-quote sample a week row shows. */
  question_count: number;
  members: TopicMember[];
  posts: DigestQuote[];
  comments: DigestQuote[];
  topic_id?: number;
  title?: string;
  status?: string | null;
  first_seen_week?: string | null;
  last_seen_week?: string | null;
  total_questions?: number;
  total_weeks?: number;
  weeks: TopicWeek[];
}

export function fetchDigestWeeks(skoolId: number): Promise<DigestWeek[]> {
  return apiGet<DigestWeek[]>(`/communities/${skoolId}/digests`);
}

/** `week` is any ISO date inside the week; the server snaps it to the Monday, so a
 *  link built from a Wednesday resolves rather than 404s. Omit for the latest. */
export function fetchDigest(skoolId: number, week?: string): Promise<Digest> {
  return apiGet<Digest>(`/communities/${skoolId}/digests/${week ?? 'latest'}`);
}

export function fetchTopics(
  skoolId: number,
  params: { since?: string; until?: string; limit?: number } = {},
): Promise<TopicRollupRow[]> {
  return apiGet<TopicRollupRow[]>(`/communities/${skoolId}/topics`, params);
}

export function fetchTopicHistory(skoolId: number, topicId: number): Promise<TopicHistory> {
  return apiGet<TopicHistory>(`/communities/${skoolId}/topics/${topicId}`);
}
